import { Check, Ellipsis, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/atomic/DropdownMenu";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { Command } from "@/models/agent/command";
import { SubAgent } from "@/models/agent/subAgent";

interface BaseAgentTableProps {
  items: Command[] | SubAgent[];
  loading: boolean;
  onEdit: (itemId: string) => void;
  handleDelete: (itemId: string, itemName: string) => Promise<void>;
}

const BaseAgentTable: React.FC<BaseAgentTableProps> = ({
  items,
  loading,
  onEdit,
  handleDelete,
}) => {
  const { workspaces } = useWorkspaces();

  const getWorkspaceName = (workspaceId: string | undefined) => {
    if (!workspaceId) return "全ワークスペース";

    const workspace = workspaces?.find((w) => w.id === workspaceId);
    return workspace?.name || workspaceId;
  };

  // 配列であることを確認
  if (!Array.isArray(items)) {
    return <div className="text-center py-8 text-red-500">データの読み込みに失敗しました</div>;
  }

  if (loading) {
    return <div className="text-center py-8">読み込み中...</div>;
  }

  return (
    <div className="w-full overflow-auto">
      <table className="w-full border-collapse table-fixed">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-[180px]">Name</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Description</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-[160px]">Workspace</th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 w-[100px]">All User</th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 w-[80px]">Active</th>
            <th className="px-4 py-3 w-[60px]" />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-b hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 text-sm truncate" title={item.name}>{item.name}</td>
              <td className="px-4 py-3 text-sm truncate" title={item.description}>
                {item.description}
              </td>
              <td className="px-4 py-3 text-sm truncate">
                {item.workspaceId ? getWorkspaceName(item.workspaceId) : "全ワークスペース"}
              </td>
              <td className="px-4 py-3 text-sm text-center">
                {item.userId ? "-" : <Check className="h-4 w-4 text-green-500 inline-block" />}
              </td>
              <td className="px-4 py-3 text-sm text-center">
                {item.enabled ? <Check className="h-4 w-4 text-green-500 inline-block" /> : "-"}
              </td>
              <td className="px-4 py-3 text-sm">
                <DropdownMenu>
                  <DropdownMenuTrigger className="p-1 hover:bg-gray-200 rounded">
                    <Ellipsis className="h-5 w-5 text-gray-600" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onEdit(item.id)}
                      className="cursor-pointer"
                    >
                      <Pencil className="h-4 w-4" />
                      編集
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDelete(item.id, item.name)}
                      className="cursor-pointer text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                      削除
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BaseAgentTable;
